/* eslint-disable no-constant-condition */

/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useRef, useState } from 'react';

import CustomButton from '../../../common/CustomButton/CustomButton';
import CustomForm from '../../../common/CustomForm/CustomForm';
import CustomTextarea from '../../../common/CustomTextarea/CustomTextarea';
import { Icon } from '../../../common/Icon/Icon';
import Markdown from '../../../common/Markdown/Markdown';
import Spinner from '../../../common/Spinner/Spinner';
import { useAlert } from '../../../core/context/alert.context';
import { useDataContext } from '../../../core/context/data.context';
import { useScreenSizeContext } from '../../../core/context/screenSize.context';
import { capitalize, isEmpty, slugify } from '../../../core/helpers';
import { useFormInput } from '../../../core/hooks/useFormInput';
import { ILink, TColor } from '../../../core/models';

const BASE_URL = `${process.env.REACT_APP_API_URL}`;
const ERROR_MSG = `This AI coach is currently experiencing technical difficulties. Please try again in a few minutes. If the issue persists, contact [support@thnk.org](mailto:support@thnk.org).`;

interface IProps {
  ai: IAnthropicAI;
}

interface IMessage {
  id: string;
  text: string;
  role: 'user' | 'assistant';
  created_at: string;
}

export interface IAnthropicAI {
  title?: string;
  btnLabel?: string;
  btnId?: string;
  initialMsg?: string;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
  model?: string;
  feedback?: {
    type?: 'external' | 'scale';
    title?: string;
    cta?: ILink;
    btnText?: string;
    btnColor?: TColor;
  };
}

function Chat({ ai }: IProps) {
  const { isMobile } = useScreenSizeContext();
  const input = useFormInput('', (value) => !isEmpty(value));
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const chatMessagesRef = useRef<HTMLDivElement | null>(null);
  const { data } = useDataContext();
  const { setAlert } = useAlert();

  const printMessageChunk = useCallback((event: any) => {
    switch (event.type) {
      case 'message_start':
        setMessages((prevState) => [
          {
            id: event.message.id,
            text: '',
            role: 'assistant',
            created_at: new Date().toLocaleString(),
          },
          ...prevState,
        ]);
        break;
      case 'content_block_delta':
        setMessages((prevState) => {
          const newMessages = prevState.map((message) => ({ ...message }));
          if (newMessages.length) {
            const msgIdx = 0;
            if (!newMessages[msgIdx]?.text) newMessages[msgIdx].text = '';
            newMessages[msgIdx].text += event.delta.text || '';
            return newMessages;
          }
          return newMessages;
        });
        break;
      default:
        break;
    }
  }, []);

  const submit = useCallback(
    async (message?: string) => {
      if (chatLoading) return;
      if (isMobile) {
        input.ref?.current?.blur();
      }
      setChatLoading(true);
      if (chatMessagesRef?.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }

      if (!message) {
        setMessages((prevState) => [
          {
            id: Date.now().toString(),
            text: input.value,
            role: 'user',
            created_at: new Date().toLocaleString(),
          },
          ...prevState,
        ]);
      }

      try {
        input.setValue('');
        const res = await fetch(`${BASE_URL}/ai_chat/anthropic/stream-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: ai.model || data?.generalData?.anthropicModel || 'claude-sonnet-4-5-20250929',
            max_tokens: ai.maxTokens || 1000,
            messages: [
              ...[...messages].reverse().map((msg) => ({ role: msg.role, content: msg.text })),
              {
                role: 'user',
                content: [
                  {
                    type: 'text',
                    text: message || input.value,
                    cache_control: { type: 'ephemeral' },
                  },
                ],
              },
            ],
            system: [
              ...(ai.systemPrompt
                ? [
                    {
                      type: 'text',
                      text: ai.systemPrompt,
                      cache_control: { type: 'ephemeral' },
                    },
                  ]
                : []),
              {
                type: 'text',
                text: new Date().toDateString(),
                cache_control: { type: 'ephemeral' },
              },
            ],
            stream: true,
            temperature: ai.temperature || 0,
          }),
        });
        const reader = res.body?.getReader();
        if (!reader) return;
        const decoder = new TextDecoder();
        let buffer: any = '';

        while (true) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              if (line.trim()) {
                const parsed = JSON.parse(line);
                printMessageChunk(parsed);
              }
            }

            if (buffer.trim()) {
              const parsed = JSON.parse(buffer);
              if (parsed.error || parsed.statusCode === 500) {
                throw new Error();
              }
            }
          } catch (err) {
            throw new Error();
          }
        }
        setChatLoading(false);
      } catch (error) {
        setChatLoading(false);
        setMessages((prevState) => [
          {
            id: Date.now().toString(),
            text: ERROR_MSG,
            role: 'assistant',
            created_at: new Date().toLocaleString(),
          },
          ...prevState,
        ]);
      }
    },
    [
      ai,
      isMobile,
      input,
      printMessageChunk,
      data?.generalData?.anthropicModel,
      messages,
      chatLoading,
    ],
  );

  const copyChat = useCallback(() => {
    const messagesString = [...messages]
      .reverse()
      .map((message) => `${capitalize(message.role)}: ${message.text}`)
      .join('\n');
    navigator.clipboard.writeText(messagesString);
    setAlert({ message: 'Chat copied to clipboard' });
  }, [messages, setAlert]);

  useEffect(() => {
    if (!ai.initialMsg) return;
    submit(ai.initialMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chatMessagesRef?.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isMobile) {
          input.setValue((prev) => `${prev}\n`);
        } else if (e.shiftKey) {
          input.setValue((prev) => `${prev}\n`);
        } else if (input.isValid) {
          submit();
        }
      }
    },
    [input, isMobile, submit],
  );

  return (
    <div className="chat">
      <div className="chat__container">
        <div className="chat__content">
          <div className="chat__chat">
            <div className="chat__header">
              {!ai.title ? null : <h2 className="h3">{ai.title}</h2>}
            </div>
            <div className="chat__messages" ref={chatMessagesRef}>
              {messages?.map((message) => (
                <div
                  className={`chat__message-container${
                    message.role === 'user' ? ' chat__message-container--user' : ''
                  }`}
                  key={message.id}
                >
                  <Markdown className="chat__message body-regular markdown">
                    {message.text.replaceAll('•', '-')}
                  </Markdown>
                </div>
              ))}
            </div>
            <CustomForm className="chat__form" inputs={[input]} onSubmit={submit}>
              <div className="chat__input-container">
                <CustomTextarea
                  className="chat__input"
                  input={input}
                  id={`chat-${slugify(ai.title || '')}`}
                  name="chat"
                  label="Type message"
                  onKeyDown={handleKeyDown}
                />
                <CustomButton
                  className="chat__control"
                  type="button"
                  title="Copy chat"
                  onClick={copyChat}
                >
                  <Icon.Copy />
                  <span className="sr-only">Copy chat</span>
                </CustomButton>
                <CustomButton className="chat__btn chat-btn" type="submit" disabled={chatLoading}>
                  {chatLoading ? <Spinner /> : <Icon.Arrow />}
                </CustomButton>
              </div>
            </CustomForm>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;
